import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { List, Header, Segment } from 'semantic-ui-react';
import { COLORS, Separator, Text, Row } from '@cuemath/comps';

import { get as getTeacherSlotData } from 'actions/teacher-slot-data';
import { getStatusData } from 'utils/slot-calendar';
import { store } from 'redux-rest-reducer';

const Wrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 350px;
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.COLA};
`;

const BorderWrapper = styled.div`
  margin-top: 10px;
  border-bottom: 1px solid ${COLORS.COLA};
`;

const StudentTitle = styled(Text)`
  width: 100%;
  padding-left: 13px;
  margin-top: 13px;
`;

const ButtonsWrapper = styled.button`
  background: ${COLORS.PRIMARY};
  color: white;
  font-size: 1em;
  margin: 0.6em;
  padding: 0.25em 1em;
  border: 2px solid ${COLORS.PRIMARY};
  border-radius: 3px;
  align-self: flex-end;
  height: 35px;
  cursor: pointer;
`;

const StudentListWrapper = styled.div`
  size: small;
`;

const LinkNav = styled(Link)`
  text-decoration: underline;
  cursor: pointer;
  color: blue;
  text-align: center;
  &:hover {
    color: blue;
  }
  &:active {
    color: blue;
  }
`;

const StudentTagWrapper = styled.div`
  padding: 6px 10px;
  border-radius: 5px;
  font-weight: 500;
  background: ${COLORS.GRAY_XXLIGHT};
  color: ${COLORS.COLA};
`;

const SlotTransitionTag = styled(Text)`
  margin-left: 5px;
`;

const SLOT_TEXT_MAP = {
  ADDED: {
    text: 'Adding student to this batch',
    color: COLORS.GRASS_DARK,
  },
  REMOVED: {
    text: 'Removing student from this batch',
    color: COLORS.RED,
  },
};

const renderStudentListHeader = headerData => {
  const { title: headerTitle, color: headerColor } = getStatusData(headerData) || {};

  return (
    <>
      <Segment basic>
        <Header as="h6" floated="right" style={{ color: headerColor }}>
          {headerTitle}
        </Header>
        <Header as="h6" floated="left">
          Slot Details
        </Header>
      </Segment>
      <BorderWrapper />
    </>
  );
};

const studentList = ({ students, isLoaded, isLoadingFailed }) => {
  const hasStudentsData =
    !!(students && Array.isArray(students) && students.length) &&
    isLoaded &&
    !isLoadingFailed;

  return (
    <StudentListWrapper>
      <Segment basic>
        {hasStudentsData && (
          <List verticalAlign="middle">
            {students.map(studentData => {
              const {
                student_id: studentId,
                student_name: studentName,
                student_slot_tag: studentSlotTag,
                slot_request: slotRequest,
              } = studentData || {};
              const { action: slotRequestAction } = slotRequest || {};

              return (
                <List.Item key={studentId}>
                  <Row justifyContent="space-between" alignItems="center">
                    <Row alignItems="center">
                      <LinkNav
                        target="_blank"
                        to={{
                          pathname: `/student-form/${studentId}`,
                        }}
                      >
                        {studentName}
                      </LinkNav>
                      {slotRequestAction && (
                        <SlotTransitionTag
                          color={SLOT_TEXT_MAP[slotRequestAction]?.color}
                        >
                          ({SLOT_TEXT_MAP[slotRequestAction]?.text})
                        </SlotTransitionTag>
                      )}
                    </Row>
                    <StudentTagWrapper>{studentSlotTag}</StudentTagWrapper>
                  </Row>
                </List.Item>
              );
            })}
          </List>
        )}
        {!hasStudentsData && (
          <List verticalAlign="middle">
            <List.Item>
              <List.Content>No Students</List.Content>
            </List.Item>
          </List>
        )}
      </Segment>
    </StudentListWrapper>
  );
};

const SlotDataPopUp = ({ actions, onTeacherClick, slotData, teacherSlotData }) => {
  const { is_locked: isSlotLocked, teacher_slot_id: teacherSlotId } = slotData || {};
  const { data: students, isLoading, isLoaded, isLoadingFailed } =
    store.get(teacherSlotData, teacherSlotId) || {};

  useEffect(() => {
    actions.getTeacherSlotData(teacherSlotId);
  }, []);

  return (
    <Wrapper>
      {renderStudentListHeader(slotData)}
      <StudentTitle>Students List</StudentTitle>

      <Separator transparent height={2} />

      {isLoading && <StudentTitle>Loading...</StudentTitle>}
      {!isLoading && studentList({ students, isLoaded, isLoadingFailed })}
      <BorderWrapper />

      <ButtonsWrapper
        type="button"
        onClick={() => onTeacherClick(isSlotLocked ? 'unlock' : 'lock', teacherSlotId)}
      >
        {isSlotLocked ? 'Unlock Slot' : 'Lock Slot'}
      </ButtonsWrapper>
      {students && Array.isArray(students) && students.length === 0 && (
        <ButtonsWrapper
          type="button"
          disabled={students && students.length ? true : false}
          onClick={() => {
            onTeacherClick('expire', teacherSlotId);
          }}
        >
          Expire
        </ButtonsWrapper>
      )}
    </Wrapper>
  );
};

const mapStateToProps = ({ teacherSlotData }) => ({ teacherSlotData });

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ getTeacherSlotData }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(SlotDataPopUp);
